import React, { useEffect, useState } from 'react';
import '../styles/pages/Players.scss';
import '../styles/components/index.scss';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import InsightsIcon from '@mui/icons-material/Insights';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import coachMockup1 from '../images/mockups/for-the-coaches1.png';
import coachMockup2 from '../images/mockups/for-the-coaches2.png';
import coachMockup3 from '../images/mockups/for-the-coaches3.png';
import coachMockup4 from '../images/mockups/for-the-coaches4.png';
import playerMockup1 from '../images/mockups/for-the-players1.png';
import playerMockup2 from '../images/mockups/for-the-players2.png';
import playerMockup3 from '../images/mockups/for-the-players3.png';
import parentMockup1 from '../images/mockups/for-the-parents1.png';
import parentMockup2 from '../images/mockups/for-the-parents2.png';
import parentMockup3 from '../images/mockups/for-the-parents3.png';
import { Helmet } from 'react-helmet';
import { useJoinModal } from '../components/JoinModalProvider';

// Custom arrow components for the slider
const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-prev-arrow`} onClick={onClick}>
      <ArrowBackIosNewIcon />
    </div>
  );
};

const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-next-arrow`} onClick={onClick}>
      <ArrowForwardIosIcon />
    </div>
  );
};

const Coaches = () => {
  const { openModal } = useJoinModal();
  // Smooth scroll function
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Create ref for the sliders
  const playerSliderRef = (slider) => {
    if (slider !== null) {
      window.playerSlider = slider;
    }
  };
  
  const coachSliderRef = (slider) => {
    if (slider !== null) {
      window.coachSlider = slider;
    }
  };
  
  const parentSliderRef = (slider) => {
    if (slider !== null) {
      window.parentSlider = slider;
    }
  };
  
  // Base settings for phone carousels
  const baseSliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    cssEase: 'ease-out',
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 20,
    waitForAnimate: false
  };
  
  // Settings for each slider with different autoplay speeds for variety
  const playerSliderSettings = {
    ...baseSliderSettings,
    autoplaySpeed: 3500 + Math.floor(Math.random() * 1000), // 3.5-4.5s
    initialSlide: Math.floor(Math.random() * 3) // random initial slide
  };
  
  const coachSliderSettings = {
    ...baseSliderSettings,
    autoplaySpeed: 4000 + Math.floor(Math.random() * 1500), // 4-5.5s
    initialSlide: Math.floor(Math.random() * 4) // random initial slide
  };
  
  const parentSliderSettings = {
    ...baseSliderSettings,
    autoplaySpeed: 3000 + Math.floor(Math.random() * 2000), // 3-5s
    initialSlide: Math.floor(Math.random() * 3) // random initial slide
  };

  // Add touch handling for carousels
  useEffect(() => {
    // Ensure slick sliders are properly initialized
    const carousels = document.querySelectorAll('.phone-carousel');
    
    carousels.forEach(carousel => {
      // Make images non-draggable to prevent interference with swipe
      const images = carousel.querySelectorAll('img');
      images.forEach(img => {
        img.draggable = false;
      });
      
      // Force slider to refresh to pick up touch events
      const slider = carousel.querySelector('.slick-slider');
      if (slider) {
        // Force refresh slick to apply touch events
        setTimeout(() => {
          // This will trigger a DOM reflow
          slider.style.display = 'none';
          // Force reflow - properly access offsetHeight
          void slider.offsetHeight;
          slider.style.display = '';
        }, 100);
      }
    });
  }, []);

  // State for FAQ accordion
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  // FAQ data
  const faqs = [
    {
      question: "How does FirstWhistle simplify team management?",
      answer: "FirstWhistle automates the most time-consuming parts of team management. From tracking player availability and managing match schedules to handling team fees and communications, our app streamlines everything into one intuitive platform."
    },
    {
      question: "How does the AI-powered team selection work?",
      answer: "Our AI analyses player statistics, historical performance, training attendance, and positional strengths to suggest optimal formations and lineups. You maintain full control while receiving intelligent recommendations based on comprehensive data analysis."
    },
    {
      question: "Can I track player development over time?",
      answer: "Yes, FirstWhistle provides detailed development tracking for each player. You can monitor progress across various metrics, set individual goals, and provide structured feedback that players and parents can access through their own accounts."
    },
    {
      question: "How does FirstWhistle handle match reports and team communications?",
      answer: "After each match, you can quickly generate comprehensive reports with player ratings, statistics, and key moments. These reports can be automatically shared with players and parents, alongside team announcements and training schedules."
    },
    {
      question: "Is FirstWhistle secure and compliant with data protection regulations?",
      answer: "Absolutely. We prioritise security and compliance with all relevant data protection laws, including GDPR. All personal information is encrypted, and we implement strict access controls to ensure data is only accessible to authorised individuals."
    }
  ];

  // To ensure correct page title
  useEffect(() => {
    document.title = "FirstWhistle - Coaches";
  }, []);

  return (
    <div className="players-page coaches-page">
      <Helmet>
        <title>FirstWhistle for Coaches | Simplify Team Management with AI</title>
        <meta name="description" content="FirstWhistle helps coaches manage teams more efficiently with AI-powered team selection, player availability tracking, performance analytics, and seamless communication tools." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/coaches" />
        <meta property="og:title" content="FirstWhistle for Coaches | Simplify Team Management with AI" />
        <meta property="og:description" content="FirstWhistle helps coaches manage teams more efficiently with AI-powered team selection, player availability tracking, performance analytics, and seamless communication tools." />
        <meta property="og:image" content="https://firstwhistle.app/images/coaches-hero-bg.jpeg" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://firstwhistle.app/coaches" />
        <meta name="twitter:title" content="FirstWhistle for Coaches | Simplify Team Management with AI" />
        <meta name="twitter:description" content="FirstWhistle helps coaches manage teams more efficiently with AI-powered team selection, player availability tracking, performance analytics, and seamless communication tools." />
        <meta name="twitter:image" content="https://firstwhistle.app/images/coaches-hero-bg.jpeg" />
        
        <link rel="canonical" href="https://firstwhistle.app/coaches" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "FirstWhistle for Coaches",
              "description": "FirstWhistle helps coaches manage teams more efficiently with AI-powered team selection, player availability tracking, performance analytics, and seamless communication tools.",
              "mainEntity": {
                "@type": "SoftwareApplication",
                "name": "FirstWhistle",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "iOS, Android",
                "description": "AI-powered football team management application for coaches to streamline team operations and enhance performance.",
                "offers": {
                  "@type": "Offer",
                  "availability": "PreOrder",
                  "price": "0",
                  "priceCurrency": "GBP"
                },
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Football Coaches and Team Managers",
                  "geographicArea": {
                    "@type": "AdministrativeArea",
                    "name": "United Kingdom"
                  }
                },
                "potentialAction": {
                  "@type": "JoinAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://firstwhistle.app/coaches",
                    "actionPlatform": [
                      "http://schema.org/DesktopWebPlatform",
                      "http://schema.org/MobileWebPlatform"
                    ]
                  },
                  "name": "Sign Up"
                },
                "featureList": [
                  "AI-powered team selection",
                  "Training plan organisation",
                  "Performance analytics dashboard",
                  "Automatic match reports",
                  "Parent communication tools"
                ]
              }
            }
          `}
        </script>
      </Helmet>

      {/* 1. Hero Section - Same structure as Players */}
      <section className="hero-section players-hero-section coaches-hero-section">
        <div className="hero-content players-hero-content">
          <h1 className="players-page-h1"><span className="page-identifier">Coaches:</span> Your Partner in Player Development</h1>
          <p className="subtext players-page-subtext">FirstWhistle helps you focus on what matters most - developing players and making football fun. Our AI-powered tools go beyond basic team management to support your coaching journey.</p>
          <div className="cta-buttons">
            <button className="cta-button primary" onClick={openModal}>Join Now</button>
            <button 
              className="cta-button secondary"
              onClick={() => scrollToSection('how-it-works')}
            >
              See How It Works
            </button>
          </div>
        </div>
        <div className="hero-background">
          <img src={require('../images/coaches-hero-bg.jpeg')} alt="Football Coach" className="players-hero-image" />
          <div className="overlay"></div>
        </div>
      </section>

      {/* 2. Features Section - Same as homepage */}
      <section className="why-matters-section" id="how-it-works">
        <h2>More Than Just Team Management</h2>
        <div className="feature-grid">
          <div className="feature-card">
            <GridViewIcon className="feature-icon" />
            <h3>Player Development Focus</h3>
            <p>Track individual progress and celebrate achievements to keep players motivated.</p>
          </div>
          
                      <div className="feature-card">
              <GroupIcon className="feature-icon" />
              <h3>Parent Education Portal</h3>
              <p>Help parents become better cheerleaders with trusted content on supporting young players' development journey.</p>
            </div>
          
          <div className="feature-card">
            <InsightsIcon className="feature-icon" />
            <h3>Performance Insights</h3>
            <p>Get actionable recommendations to help each player reach their potential.</p>
          </div>
          
          <div className="feature-card">
            <EmojiEventsIcon className="feature-icon" />
            <h3>Fun & Rewards</h3>
            <p>Keep the excitement alive with achievements, challenges and celebrations.</p>
          </div>
        </div>
      </section>

      {/* 3. Key Benefits Section - Coach's Tools */}
      <section className="who-its-for-section">
        <h2>Your Complete Coaching Companion</h2>
        <div className="persona-cards">
          <div className="persona-card">
            <div className="persona-avatar coach">
              <SportsSoccerIcon style={{ fontSize: '40px', color: 'white', opacity: 0.9 }} />
            </div>
            <h3>Player Development Hub</h3>
            <p>Set individual goals, track progress, and celebrate milestones. Create personalised development plans that keep players engaged and excited about their journey.</p>
          </div>
                      <div className="persona-card">
              <div className="persona-avatar coach" style={{ backgroundColor: '#5c6bc0' }}>
                <AssessmentIcon style={{ fontSize: '40px', color: 'white', opacity: 0.9, position: 'relative', zIndex: 2 }} />
              </div>
              <h3>Smart Coaching Tools <span style={{ fontSize: '0.8em', color: '#ffd700' }}>(Coming Soon)</span></h3>
              <p>Use AI-powered insights to identify strengths, track improvement, and create engaging training sessions that develop both skills and confidence.</p>
            </div>
          <div className="persona-card">
            <div className="persona-avatar coach" style={{ backgroundColor: '#26a69a' }}>
              <PhotoLibraryIcon style={{ fontSize: '40px', color: 'white', opacity: 0.9, position: 'relative', zIndex: 2 }} />
            </div>
            <h3>Team Culture Builder</h3>
            <p>Foster team spirit with interactive challenges, achievements, and rewards. Make every training session and match an opportunity for growth and fun.</p>
          </div>
        </div>
      </section>

      {/* 5. Screenshots Section - Same as audience-section on homepage */}
      <section className="screenshots-section" id="screenshots-section">
        <h2>Powerful tools for every coaching challenge</h2>
        <div className="audience-section">
          <div className="audience-columns">
            <div className="audience-column">
              <div className="phone-carousel">
                <Slider ref={playerSliderRef} {...playerSliderSettings} key="player-slider">
                  <div className="phone-slide">
                    <img src={playerMockup1} alt="FirstWhistle for Players - Dashboard" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={playerMockup2} alt="FirstWhistle for Players - Stats" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={playerMockup3} alt="FirstWhistle for Players - Awards" className="audience-phone" draggable="false" />
                  </div>
                </Slider>
              </div>
              <h3>When managing <span style={{ color: '#ffd700' }}>Players</span></h3>
              <p>Motivate your squad by giving them insights into their progress. Players stay engaged through stats tracking and rewards you control.</p>
              <ul className="feature-list">
                <li><CheckCircleOutlineIcon className="check-icon" /> Control who sees team selections and when they're published</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Share player statistics with appropriate context and insights</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Award achievements to motivate your squad throughout the season</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Deliver targeted feedback to help players develop their skills</li>
              </ul>
            </div>
            <div className="audience-column">
              <div className="phone-carousel">
                <Slider ref={coachSliderRef} {...coachSliderSettings} key="coach-slider">
                  <div className="phone-slide">
                    <img src={coachMockup1} alt="FirstWhistle for Coaches - Team Selection" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={coachMockup2} alt="FirstWhistle for Coaches - Analytics" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={coachMockup3} alt="FirstWhistle for Coaches - Availability" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={coachMockup4} alt="FirstWhistle for Coaches - Management" className="audience-phone" draggable="false" />
                  </div>
                </Slider>
              </div>
              <h3>Your <span style={{ color: '#ffd700' }}>Coaching</span> Dashboard</h3>
              <p>Command central for your team's management. AI-powered insights help you make tactical decisions based on real data, not just intuition.</p>
              <ul className="feature-list">
                <li><CheckCircleOutlineIcon className="check-icon" /> Optimise formations and team selection based on player performance data</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Analyse opposition patterns and identify tactical advantages</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Track player development with custom metrics and progress charts</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Plan training sessions targeting specific team improvement areas</li>
              </ul>
            </div>
            <div className="audience-column">
              <div className="phone-carousel">
                <Slider ref={parentSliderRef} {...parentSliderSettings} key="parent-slider">
                  <div className="phone-slide">
                    <img src={parentMockup1} alt="FirstWhistle for Parents - Fixtures" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={parentMockup2} alt="FirstWhistle for Parents - Payments" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={parentMockup3} alt="FirstWhistle for Parents - Development" className="audience-phone" draggable="false" />
                  </div>
                </Slider>
              </div>
              <h3>Simplified <span style={{ color: '#ffd700' }}>Parent</span> Communication</h3>
              <p>Reduce admin time with streamlined parent communications. Schedule updates, fee reminders, and match details automatically.</p>
              <ul className="feature-list">
                <li><CheckCircleOutlineIcon className="check-icon" /> Send automatic fixture alerts with location maps and weather updates</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Track fee payments and send gentle reminders when payments are due</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Distribute performance feedback to parents with customised privacy settings</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Broadcast team announcements with confirmation tracking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <div className="partners">
        <div className="partner-logos">
          <a href="https://playsport.co.uk" target="_blank" rel="noopener noreferrer"><img src={require('../images/partners/Playsport-logo.png')} alt="Playsport" /></a>
          <a href="https://kickitout.co.uk" target="_blank" rel="noopener noreferrer"><img src={require('../images/partners/kick-it-out-logo.png')} alt="Kick it out" /></a>
          <a href="https://www.onlineticketseller.com/" target="_blank" rel="noopener noreferrer"><img src={require('../images/partners/online-ticket-seller.png')} alt="Online Ticket Seller" /></a>
        </div>
      </div>

      {/* 6. FAQ Section - Same as Players */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {faqs.map((faq, index) => (
            <div className="faq-item" key={index}>
              <div 
                className="faq-question" 
                onClick={() => toggleFaq(index)}
              >
                <h3>{faq.question}</h3>
                {expandedFaq === index ? <ExpandLessIcon className="expand-icon" /> : <ExpandMoreIcon className="expand-icon" />}
              </div>
              <div className={`faq-answer ${expandedFaq === index ? 'expanded' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 7. Social Proof Section - Same as Players */}
      <section className="social-proof-section">
        <h2>What coaches are saying about FirstWhistle</h2>
        <div className="testimonials">
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"FirstWhistle has completely transformed how I manage our team. The AI team selection suggestions are brilliant, and I've saved hours on admin tasks every week."</p>
            <div className="author">- David, U16 Head Coach</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"The player availability tracking alone is worth it. No more chasing players for responses - the app sends reminders and collates everything automatically."</p>
            <div className="author">- Lisa, Women's Team Manager</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"Being able to track player development over the season has improved our training focus. The performance analytics have helped us identify patterns we'd never have spotted otherwise."</p>
            <div className="author">- Paul, Sunday League Coach</div>
          </div>
        </div>
      </section>

      {/* 8. Call to Action Section */}
      <section className="cta-section">
        <h2>Coach smarter, not harder.</h2>
        <p>Be the first to experience FirstWhistle when we launch and transform how you manage your football team.</p>
        <div className="cta-buttons">
          <button className="cta-button primary" onClick={openModal}>Join Now</button>
          <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
            <InstagramIcon /> Follow Us on Instagram
          </a>
        </div>
      </section>

      {/* 9. Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <p className="tagline">FirstWhistle – Where every coach counts.</p>
          </div>
          <div className="footer-navigation">
            <div className="footer-links">
              <Link to="/about">About</Link>
              <Link to="/players">Players</Link>
              <Link to="/coaches">Coaches</Link>
              <Link to="/parents">Parents</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                <InstagramIcon className="social-icon" />
              </a>
              <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                <FacebookIcon className="social-icon" />
              </a>
            </div>
          </div>
          <div className="footer-legal">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Coaches; 